import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';

import type { Vendor } from '../../types';
import { isNewlyReleasedSeason } from '../../shared/helpers/categoryHelpers';
import type { SvpEpisode } from '../../shared/helpers/assetHelpers';
import { getAssetFlightTimesStart } from '../../shared/helpers/assetHelpers';
import { unleash } from '../../services/unleash';

import { useServerTime } from './useServerTime';
import { useSeriesWatchtime } from './useSeriesWatchtime';
import { useDefaultEpisode } from './useDefaultEpisode';

const getVariant = (episode: SvpEpisode, serverTime: number) => {
  if (isNewlyReleasedSeason(episode, serverTime)) {
    return episode.series.seasonNumber === 1 ? 'series' : 'season';
  }

  return undefined;
};

export function isUpcoming(flightTimesStart: number): boolean {
  const start = flightTimesStart;

  if (!start) {
    return false;
  }

  const nowUnixTimestamp = Date.now() / 1000;

  return nowUnixTimestamp < start;
}

export const useCategoryRelease = ({
  category,
  vendor,
}: {
  category: SvpCategoryAdditional;
  vendor: Vendor;
}) => {
  const { serverTime } = useServerTime();
  const isWatchtimeEnabled = unleash.isEnabled('watchtime');

  const { data: defaultEpisode } = useDefaultEpisode({
    category,
    vendor,
  });
  const { isFetched: isWatchtimeFetched, watchtime } = useSeriesWatchtime({
    categoryId: category.id,
    vendor,
  });

  const flightTimesStart =
    defaultEpisode && getAssetFlightTimesStart(defaultEpisode);

  if (isWatchtimeEnabled && !isWatchtimeFetched) {
    return {
      variant: undefined,
    };
  }

  if (
    flightTimesStart &&
    isWatchtimeEnabled &&
    watchtime?.time &&
    watchtime?.updatedAt > flightTimesStart
  ) {
    return {
      variant: undefined,
    };
  }

  if (flightTimesStart && isUpcoming(flightTimesStart)) {
    return {
      variant: 'upcoming',
    } as const;
  }

  return {
    variant: defaultEpisode && getVariant(defaultEpisode, serverTime),
  } as const;
};
